import React, { useState, useEffect, Suspense } from 'react'
import { Outlet } from 'react-router-dom'
import { Menu, AlertCircle, RefreshCw, Gem, Plus } from 'lucide-react'
import logo from '@/assets/logo.png'
import { AppSidebar } from './AppSidebar'
import { UserAvatarMenu } from '@/components/common/UserAvatarMenu'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { agentAPI, type AgentStats } from '@/features/agent/api/agentApi'

const QuickDepositModal = React.lazy(() => 
  import('@/components/common/QuickDepositModal').then(m => ({ default: m.QuickDepositModal }))
)

export interface AgentContextType {
  agentStats: AgentStats | null
  refetchAgentStats: () => void
}

export const AppLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [stats, setStats] = useState<AgentStats | null>(null)
  const [status, setStatus] = useState<'loading' | 'registered' | 'error'>('loading')
  const [errorMsg, setErrorMsg] = useState('')
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // State mở modal nạp tiền từ header
  const [isDepositOpen, setIsDepositOpen] = useState(false)

  const refetchAgentStats = () => setRefreshTrigger(c => c + 1)

  useEffect(() => {
    let active = true
    const checkAgentStatus = async () => {
      if (!stats) {
        setStatus('loading')
      }
      try {
        const res = await agentAPI.getStats()
        if (active) {
          setStats(res)
          setStatus('registered')
        }
      } catch (err: any) {
        if (!active) return
        setErrorMsg(err.response?.data?.message || 'Có lỗi xảy ra khi kiểm tra thông tin ví')
        setStatus('error')
      }
    }

    checkAgentStatus()
    return () => {
      active = false
    }
  }, [refreshTrigger])

  if (status === 'loading') {
    return <LoadingSpinner />
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="bg-white border border-red-100 rounded-2xl p-6 max-w-md w-full shadow-sm text-center">
          <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Lỗi kết nối hệ thống</h2>
          <p className="text-sm text-gray-600 mb-6">{errorMsg}</p>
          <button
            onClick={refetchAgentStats}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-[#0068FF] hover:bg-[#0052cc] rounded-xl transition-all shadow-sm shadow-blue-100"
          >
            <RefreshCw className="w-4 h-4" /> Thử lại
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AppSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-gray-200 px-4 flex items-center justify-between h-[60px]">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-md hover:bg-gray-100">
            <Menu className="w-5 h-5" />
          </button>
          
          {/* Mobile view brand name */}
          <a href="https://hocnhe.com" className="lg:hidden flex items-center gap-2 text-left">
            <img src={logo} alt="Học Nhé" className="w-6 h-6 object-contain" />
            <div className="flex flex-col">
              <span className="text-[12px] font-black tracking-tight leading-none text-[#0068FF]">
                Học Nhé
              </span>
              <span className="text-[7.5px] text-[#E65F00] font-extrabold tracking-wider mt-0.5 uppercase">
                Học cùng nhau
              </span>
            </div>
          </a>

          {/* Desktop view brand name: Empty on desktop since sidebar has it */}
          <div className="hidden lg:block" />

          {/* Right Header controls */}
          <div className="flex items-center gap-3">
            {stats && (
              /* Integrated Wallet Widget */
              <div className="flex items-center bg-white border border-[#E65F00] rounded-xl overflow-hidden shadow-sm h-8">
                {/* Balance Display */}
                <div className="flex items-center gap-1.5 px-3 h-full bg-slate-50/50">
                  <Gem className="w-3.5 h-3.5 text-[#E65F00]" />
                  <span className="text-xs font-black text-[#E65F00] font-mono">
                    {stats.credit_balance.toLocaleString('vi-VN')}
                  </span>
                </div>
                
                {/* Divider Line */}
                <div className="w-px h-full bg-[#E65F00]/30" />
                
                {/* Quick Plus Button */}
                <button
                  onClick={() => setIsDepositOpen(true)}
                  className="flex items-center justify-center w-8 h-full bg-white hover:bg-slate-50 text-[#0068FF] transition-colors cursor-pointer shrink-0"
                  title="Nạp tiền"
                >
                  <Plus className="w-3.5 h-3.5 text-[#0068FF] stroke-[3]" />
                </button>
              </div>
            )}
            
            <UserAvatarMenu />
          </div>
        </header>

        <main className="flex-1 px-4 py-5 sm:p-6 overflow-x-hidden">
          <Outlet context={{ agentStats: stats, refetchAgentStats } satisfies AgentContextType} />
        </main>
      </div>

      {/* Embedded Quick Deposit Modal */}
      <Suspense fallback={null}>
        {isDepositOpen && (
          <QuickDepositModal
            isOpen={isDepositOpen}
            onClose={() => {
              setIsDepositOpen(false)
              refetchAgentStats()
            }}
            initialTab="deposit"
            refetchStats={refetchAgentStats}
            agentStats={stats}
          />
        )}
      </Suspense>
    </div>
  )
}
